import { ref } from 'vue';
import { createI18n } from 'vue-i18n';
import defaultConfig from '@/config/defaultConfig';
import { getFromCache, setToCache } from './cache';
import dayjs from '@/plugins/dayjs';

// Standard ISO 3166-1 alpha-2 country codes
const ISO_COUNTRY_CODES = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW'
];

const messages = () => import('@/locales/en.json');

const { fallbackLocale, supportedLanguages, selectedLanguage } =
  defaultConfig.i18n;

// State
const loadedLanguages = ref([]);
const currentLanguage = ref(selectedLanguage);

// i18n instance
export const i18n = createI18n({
  legacy: false,
  locale: currentLanguage.value,
  fallbackLocale,
  warnHtmlMessage: false,
  messages: {
    en: messages
  }
});

const loadLanguageAsync = async (lang) => {
  // If language is already loaded, just switch to it
  if (loadedLanguages.value.includes(lang)) {
    return setI18nLanguage(lang);
  }

  // Check if language is already cached
  const cachedData = getFromCache(lang);
  if (cachedData) {
    i18n.global.setLocaleMessage(lang, cachedData);
    loadedLanguages.value.push(lang);
    return setI18nLanguage(lang);
  }

  // Load language messages from server or local files
  const messages = await loadMessages(lang);
  if (messages) {
    i18n.global.setLocaleMessage(lang, messages);
    loadedLanguages.value.push(lang);
    setI18nLanguage(lang);
  }
};

const loadMessages = async (lang) => {
  // Try to load from remote server first
  try {
    const { default: apiClient } = await import('@/services/api');
    const response = await apiClient.get(
      `${import.meta.env.VITE_BM_ASSETS_HOST}/translations/backoffice/locales/${lang}.json`
    );

    if (response?.data) {
      setToCache(lang, response.data); // Cache for future use
      return response.data;
    }
  } catch (error) {
    console.error(`Error loading ${lang} from server:`, error);
  }

  // Fallback to local files if server request fails
  try {
    const response = await import(`../locales/${lang}.json`);
    return response.default;
  } catch (error) {
    console.error(`Error loading local fallback ${lang}:`, error);
    return null;
  }
};

const setI18nLanguage = (lang) => {
  i18n.global.locale.value = lang;
  document.querySelector('html').setAttribute('lang', lang);
  currentLanguage.value = lang;
};

const setLanguage = (lang) => {
  dayjs.locale(lang);
  dayjs.updateLocale(lang, { weekStart: 1 });
  localStorage.setItem('sesc.selectedLanguage', lang);
  loadLanguageAsync(lang);
};

const getCountries = (locale) => {
  let normalizedLocale = 'en';

  if (locale) {
    normalizedLocale = locale.replace('_', '-');
    try {
      new Intl.DisplayNames([normalizedLocale], { type: 'region' });
    } catch {
      const langOnly = normalizedLocale.split('-')[0];
      try {
        new Intl.DisplayNames([langOnly], { type: 'region' });
        normalizedLocale = langOnly;
      } catch {
        normalizedLocale = 'en';
      }
    }
  }

  const regionNames = new Intl.DisplayNames([normalizedLocale], {
    type: 'region'
  });

  return Object.fromEntries(
    ISO_COUNTRY_CODES.map((code) => {
      try {
        return [code, regionNames.of(code)];
      } catch {
        return [code, code];
      }
    })
  );
};

const detectLanguage = () => {
  const browserLanguage = navigator.language.replace('-', '_');
  const formattedBrowserLang = browserLanguage.substring(0, 2);

  if (selectedLanguage) return selectedLanguage;
  if (supportedLanguages.includes(browserLanguage)) return browserLanguage;
  if (supportedLanguages.includes(formattedBrowserLang))
    return formattedBrowserLang;

  for (const lang of supportedLanguages) {
    if (lang.substr(0, 2) === formattedBrowserLang) return lang;
  }

  return fallbackLocale;
};

// initialize when imported once
loadLanguageAsync(detectLanguage());

export const useInternationalization = () => {
  return {
    i18n,
    currentLanguage,
    loadedLanguages,
    detectLanguage,
    setLanguage,
    getCountries,
    loadLanguageAsync
  };
};
